import type { AdjustmentState, CropRect, EditorEventCallback, EditorEventType, EditorSnapshot } from './types';
import { DEFAULT_ADJUSTMENTS } from './types';
import { CanvasManager } from './CanvasManager';
import { HistoryManager } from './HistoryManager';
import { runPipeline } from '../processing/pipeline';
import { rotate90, rotateFree } from '../processing/rotate';
import { flipHorizontal, flipVertical } from '../processing/flip';
import { cropImageData } from '../processing/crop';
import { resizeImageData } from '../processing/resize';

export class ImageEditorEngine {
  private cm: CanvasManager;
  private history: HistoryManager;
  private baseImage: ImageData | null = null;
  private originalImage: ImageData | null = null;
  private adjustments: AdjustmentState = { ...DEFAULT_ADJUSTMENTS };
  private rafId: number | null = null;
  private listeners = new Map<EditorEventType, Set<EditorEventCallback>>();

  constructor(cm: CanvasManager) {
    this.cm = cm;
    this.history = new HistoryManager(() => this.emit('history-changed'));

    window.addEventListener('resize', () => {
      if (this.baseImage) this.cm.refreshDisplay();
    });
  }

  on(event: EditorEventType, cb: EditorEventCallback): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(cb);
  }

  private emit(event: EditorEventType): void {
    this.listeners.get(event)?.forEach((cb) => cb());
  }

  get canUndo(): boolean { return this.history.canUndo; }
  get canRedo(): boolean { return this.history.canRedo; }
  get hasImage(): boolean { return this.baseImage !== null; }
  get imageWidth(): number { return this.baseImage?.width ?? 0; }
  get imageHeight(): number { return this.baseImage?.height ?? 0; }

  loadImage(img: HTMLImageElement): void {
    const canvas = new OffscreenCanvas(img.naturalWidth, img.naturalHeight);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    this.baseImage = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
    this.originalImage = new ImageData(
      new Uint8ClampedArray(this.baseImage.data),
      this.baseImage.width,
      this.baseImage.height,
    );

    this.adjustments = { ...DEFAULT_ADJUSTMENTS };
    this.history.clear();
    this.pushSnapshot();

    this.cm.setSize(img.naturalWidth, img.naturalHeight);
    this.cm.show();
    this.renderPipeline();
    this.emit('image-loaded');
  }

  setAdjustment<K extends keyof AdjustmentState>(key: K, value: AdjustmentState[K]): void {
    this.adjustments[key] = value;
    this.scheduleRender();
    this.emit('adjustment-changed');
  }

  resetAdjustments(): void {
    this.adjustments = { ...DEFAULT_ADJUSTMENTS };
    this.scheduleRender();
    this.emit('adjustment-changed');
  }

  resetAll(): void {
    if (!this.originalImage) return;
    this.baseImage = new ImageData(
      new Uint8ClampedArray(this.originalImage.data),
      this.originalImage.width,
      this.originalImage.height,
    );
    this.adjustments = { ...DEFAULT_ADJUSTMENTS };
    this.history.clear();
    this.pushSnapshot();
    this.cm.setSize(this.baseImage.width, this.baseImage.height);
    this.renderPipeline();
    this.emit('image-updated');
  }

  private scheduleRender(): void {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.renderPipeline();
    });
  }

  private renderPipeline(): void {
    if (!this.baseImage) return;

    // Handle free rotation: apply to base, then run pixel pipeline
    let source = this.baseImage;
    if (this.adjustments.rotation !== 0) {
      source = rotateFree(source, this.adjustments.rotation);
    }

    const result = runPipeline(source, this.adjustments);
    this.cm.drawImageData(result);
  }

  // --- Destructive operations ---

  rotate90(direction: 1 | -1): void {
    if (!this.baseImage) return;
    this.baseImage = rotate90(this.baseImage, direction);
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  rotate180(): void {
    if (!this.baseImage) return;
    this.baseImage = rotate90(rotate90(this.baseImage, 1), 1);
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  flipH(): void {
    if (!this.baseImage) return;
    this.baseImage = flipHorizontal(this.baseImage);
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  flipV(): void {
    if (!this.baseImage) return;
    this.baseImage = flipVertical(this.baseImage);
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  applyFreeRotation(): void {
    if (!this.baseImage || this.adjustments.rotation === 0) return;
    this.baseImage = rotateFree(this.baseImage, this.adjustments.rotation);
    this.adjustments.rotation = 0;
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  applyCrop(rect: CropRect): void {
    if (!this.baseImage) return;
    this.baseImage = cropImageData(this.baseImage, rect);
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  applyResize(width: number, height: number): void {
    if (!this.baseImage) return;
    this.baseImage = resizeImageData(this.baseImage, width, height);
    this.pushSnapshot();
    this.renderPipeline();
    this.emit('image-updated');
  }

  // --- Crop mode ---

  private _cropMode = false;

  get cropMode(): boolean { return this._cropMode; }

  toggleCropMode(): void {
    this._cropMode = !this._cropMode;
    this.emit('crop-mode-changed');
  }

  exitCropMode(): void {
    this._cropMode = false;
    this.emit('crop-mode-changed');
  }

  // --- Undo/Redo ---

  private pushSnapshot(): void {
    if (!this.baseImage) return;
    const snapshot: EditorSnapshot = {
      imageData: new ImageData(
        new Uint8ClampedArray(this.baseImage.data),
        this.baseImage.width,
        this.baseImage.height,
      ),
      width: this.baseImage.width,
      height: this.baseImage.height,
    };
    this.history.push(snapshot);
  }

  undo(): void {
    const snapshot = this.history.undo();
    if (snapshot) {
      this.baseImage = new ImageData(
        new Uint8ClampedArray(snapshot.imageData.data),
        snapshot.width,
        snapshot.height,
      );
      this.renderPipeline();
      this.emit('image-updated');
    }
  }

  redo(): void {
    const snapshot = this.history.redo();
    if (snapshot) {
      this.baseImage = new ImageData(
        new Uint8ClampedArray(snapshot.imageData.data),
        snapshot.width,
        snapshot.height,
      );
      this.renderPipeline();
      this.emit('image-updated');
    }
  }

  // --- Export (replaces download method) ---

  async getExportBlob(format: string, quality?: number): Promise<Blob> {
    if (!this.baseImage) throw new Error('No image loaded');

    // Render current state to an offscreen canvas for export
    let source = this.baseImage;
    if (this.adjustments.rotation !== 0) {
      source = rotateFree(source, this.adjustments.rotation);
    }
    const result = runPipeline(source, this.adjustments);

    const offscreen = new OffscreenCanvas(result.width, result.height);
    const ctx = offscreen.getContext('2d')!;
    ctx.putImageData(result, 0, 0);

    const mimeMap: Record<string, string> = {
      'png': 'image/png',
      'jpeg': 'image/jpeg',
      'jpg': 'image/jpeg',
      'webp': 'image/webp',
    };
    const mimeType = mimeMap[format] || 'image/png';
    const q = quality ?? (format === 'jpeg' || format === 'jpg' ? 0.92 : undefined);

    return offscreen.convertToBlob({ type: mimeType, quality: q });
  }
}
